import { IComparer } from "./IComparer";
import { NAMESPACE } from "./_Namespace";

export class DeepObjectValueComparer implements IComparer<any>
{
    public static classNamespace = NAMESPACE + "DeepObjectValueComparer";
    public static className = "DeepObjectValueComparer";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Global Variables

    public static readonly Default = new DeepObjectValueComparer();

    // #endregion

    // #region Public Methods

    public compare(x: any, y: any): -1 | 0 | 1
    {
        // https://stackoverflow.com/questions/1068834/object-comparison-in-javascript
        let
            leftChain: any[],
            rightChain: any[];

        function compare2Objects(x: any, y: any): -1 | 0 | 1
        {
            let
                p: any;

            // remember that NaN === NaN returns false
            // and isNaN(undefined) returns true
            if (isNaN(x) && isNaN(y) && typeof x === "number" && typeof y === "number")
                return 0;

            // Compare primitives and functions.     
            // Check if both arguments link to the same object.
            // Especially useful on the step where we compare prototypes
            if (x === y)
                return 0;

            // Works in case when functions are created in constructor.
            // Comparing dates is a common scenario. Another built-ins?
            // We can even handle functions passed across iframes
            if ((typeof x === "function" && typeof y === "function") ||
                (x instanceof Date && y instanceof Date) ||
                (x instanceof RegExp && y instanceof RegExp) ||
                (x instanceof String && y instanceof String) ||
                (x instanceof Number && y instanceof Number))
            {
                if (x.toString() === y.toString())
                    return 0;

                return x.toString() < y.toString() ? -1 : 1;
            }

            // At last checking prototypes as good as we can
            if (!(x instanceof Object && y instanceof Object))
                return -1;

            if (Object.prototype.isPrototypeOf.call(x, y) || Object.prototype.isPrototypeOf.call(y, x))
                return -1;

            if (x.constructor !== y.constructor)
                return -1;

            if (x.prototype !== y.prototype)
                return -1;

            // Check for infinitive linking loops
            if (leftChain.indexOf(x) > -1 || rightChain.indexOf(y) > -1)
                return -1;

            // Quick checking of one object being a subset of another.
            // todo: cache the structure of arguments[0] for performance
            for (p in y)
            {
                const
                    typeofYP = typeof y[p],
                    typeofXP = typeof x[p];

                if (!Object.prototype.hasOwnProperty.call(y, p) && Object.prototype.hasOwnProperty.call(x, p))
                    return -1;
                else if (Object.prototype.hasOwnProperty.call(y, p) && !Object.prototype.hasOwnProperty.call(x, p))
                    return 1;
                else if (typeofYP !== typeofXP)
                    return (typeofXP < typeofYP) ? -1 : 1;
            }

            for (p in x)
            {
                const
                    typeofXP = typeof x[p],
                    typeofYP = typeof y[p];

                if (Object.prototype.hasOwnProperty.call(x, p) && !Object.prototype.hasOwnProperty.call(y, p))
                    return -1;
                else if (!Object.prototype.hasOwnProperty.call(x, p) && Object.prototype.hasOwnProperty.call(y, p))
                    return 1;
                else if (typeofXP !== typeofYP)
                    return (typeofXP < typeofYP) ? -1 : 1;

                switch (typeof (x[p]))
                {
                    case "object":
                    case "function":

                        leftChain.push(x);
                        rightChain.push(y);

                        const
                            result = compare2Objects(x[p], y[p]);

                        if (result !== 0)
                            return result;

                        leftChain.pop();
                        rightChain.pop();
                        break;

                    default:
                        if (x[p] !== y[p])
                            return x[p] < y[p] ? -1 : 1;
                        break;
                }
            }

            return 0;
        }

        leftChain = []; //Todo: this can be cached
        rightChain = [];

        return compare2Objects(x, y);
    }

    // #endregion
}